<script lang="ts">
	import type { HTMLInputAttributes } from "svelte/elements";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { emailPattern } from "@core/utils/email.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const { ...rest }: HTMLInputAttributes = $props();
	const { tenant } = getGlobalTemplateProps();

	const type = $derived(rest.type ?? "text");
	const customPattern: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, string>> = {
		email: emailPattern,
	};
	const pattern = $derived(rest.pattern ?? customPattern[type]);

	const customValidationMessage: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString>> = {
		email: {
			[Locale.cs]: "E-mailová adresa",
			[Locale.en]: "E-mail address",
		},
		url: {
			[Locale.cs]: "URL adresa (včetně https://)",
			[Locale.en]: "URL address (including https://)",
		},
	};

	function validate({ currentTarget }: Event & { currentTarget: EventTarget & HTMLInputElement }): void {
		currentTarget.setCustomValidity("");

		if (currentTarget.validity.valid) {
			currentTarget.setCustomValidity("");
		} else {
			const customMessage = customValidationMessage[type]?.[tenant.locale];
			if (customMessage) {
				currentTarget.setCustomValidity(customMessage);
			}
		}
	}
</script>

<input
	{...rest}
	class={[
		"min-w-0 shadow-sm border-transparent [&:user-invalid]:outline-primary transition-[outline,text] duration-300 focus:outline-grey-700 focus:[&:user-invalid]:outline-grey-700 outline-transparent outline-2 outline outline-offset-0 bg-white rounded-full py-3 px-3",
		{
			"checked:before:bg-primary disabled:bg-gray-200 peer relative h-[.9rem] w-[.9rem] shrink-0 appearance-none rounded-full border ml-2 lg:ml-3 bg-white before:absolute before:inset-0 before:m-auto before:block before:h-[8px] before:w-[8px] before:shrink-0 before:rounded-full before:transition-colors before:duration-200 before:content-[''] after:relative after:block after:h-full after:w-full after:rounded-full after:content-[''] sm:h-4 sm:w-4":
				["checkbox", "radio"].includes(type),
			"min-h-[3.125rem]": !["radio", "checkbox"].includes(type),
		},
		rest.class,
	]}
	{pattern}
	oninput={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
		validate(event);
		rest.oninput?.(event);
	}}
	oninvalid={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
		validate(event);
		rest.oninvalid?.(event);
	}}
/>
